import { easings, useTransition, type UseTransitionProps } from '@react-spring/web';
import type { MouseEventHandler } from 'react';
import {
  createContext,
  type CSSProperties,
  forwardRef,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import FocusLock from 'react-focus-lock';

import { Portal } from '../Portal';
import { OverlayWrapper, Wrapper } from './styles';

import { useCallbackRef } from '../../hooks/useCallbackRef';
import { useIsPopout } from '../../hooks/useCurrentDocument';
import { useForwardedRef } from '../../hooks/useForwardedRef';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useOnKeyDown } from '../../hooks/useOnKeyPress';
import type { BoxProps } from '../Core';

const MODAL_TRANSITION: UseTransitionProps = {
  from: { opacity: 0, transform: 'scale(0.9) translateY(12px)' },
  enter: { opacity: 1, transform: 'scale(1) translateY(0)' },
  leave: { opacity: 0, transform: 'scale(0.9) translateY(12px)' },
  config: {
    duration: 300,
    easing: easings.easeOutBack,
  },
  delay: 100,
};

export type ModalProps = PropsWithChildren<
  {
    isOpen: boolean;
    close(): void;

    autoFocusFirstElement?: boolean;
    /** Disable Focus Lock when it interferes
     * (e.g. seen in certain scenarios, like monaco-editor right-click menus) */
    disableFocusLock?: boolean;
    /** Still renders a transparent overlay */
    useTransparentOverlay?: boolean;
    usePortal?: boolean;
    portalID?: string;
    closeOnEscape?: boolean;
    closeOnClickOutside?: boolean;
    width?: number | string;
    height?: number | string;
    portalElement?: HTMLElement;
    /**
     * If provided, modal will float with the margin top specified, and grow downwards.
     */
    mt?: CSSProperties['marginTop'];

    style?: CSSProperties;
    preventClosing?: boolean;
    /** Called when the close animation completes */
    onCloseComplete?: () => void;
  } & Omit<BoxProps, 'onChange'>
>;

export const MODAL_PORTAL_ID = 'modal-portal-id';

// InnerModal component props interface
type InnerModalProps = Omit<ModalProps, 'usePortal' | 'portalID' | 'portalElement'> & {
  ref: React.Ref<HTMLDivElement>;
};

export const Modal = forwardRef<HTMLDivElement | null, ModalProps>(
  (
    {
      usePortal = true,
      portalID = MODAL_PORTAL_ID,
      portalElement,
      onCloseComplete,

      close,
      useTransparentOverlay = false,
      isOpen,

      ...props
    },
    ref
  ) => {
    const isPopout = useIsPopout();
    const transition = useTransition(isOpen, MODAL_TRANSITION);
    const overlayRef = useRef<HTMLDivElement>(null);

    const MaybePortal = usePortal ? Portal : MockPortal;

    const handleOverlayMouseDown: MouseEventHandler<HTMLDivElement> = useCallback(
      e => {
        if (props.closeOnClickOutside && !props.preventClosing && e.target === overlayRef.current) {
          close();
        }
      },
      [close, props.closeOnClickOutside, props.preventClosing]
    );

    return (
      <MaybePortal portalId={portalID} portalElement={portalElement} createTopLevelPortal={true}>
        <ModalContext.Provider value={modalContextDefaultValue}>
          <Overlay
            ref={overlayRef}
            useTransparentOverlay={useTransparentOverlay}
            isOpen={isOpen}
            isCentered={!props.mt}
            onMouseDown={handleOverlayMouseDown}
            onCloseComplete={onCloseComplete}
          >
            {isPopout
              ? // No transitions for popout mode
                isOpen && (
                  <InnerModal
                    ref={ref}
                    isOpen={isOpen}
                    useTransparentOverlay={useTransparentOverlay}
                    close={close}
                    {...props}
                  />
                )
              : // Use transitions for normal mode
                transition(
                  (style, item, t) =>
                    item && (
                      <InnerModal
                        ref={ref}
                        isOpen={isOpen}
                        useTransparentOverlay={useTransparentOverlay}
                        style={{ ...style, ...props.style }}
                        key={t.key}
                        close={close}
                        {...props}
                      />
                    )
                )}
          </Overlay>
        </ModalContext.Provider>
      </MaybePortal>
    );
  }
);

// Use when we don't want a portal, but still allows us to set portalId
const MockPortal = ({ children }: PropsWithChildren<{ portalId: string }>) => <>{children}</>;

export const Overlay = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{
    useTransparentOverlay?: boolean;
    isOpen: boolean;
    isCentered?: boolean;
    onMouseDown?: MouseEventHandler<HTMLDivElement>;
    onCloseComplete?: () => void;
  }>
>(function ({ useTransparentOverlay = false, isOpen, isCentered = true, children, onMouseDown, onCloseComplete }, ref) {
  const isPopout = useIsPopout();
  const onDestroyedRef = useCallbackRef(() => {
    if (!isOpen) {
      onCloseComplete?.();
    }
  });
  const overlayTransition = useMemo(
    () =>
      ({
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
        onDestroyed: onDestroyedRef,
        config: (_isOpen, _i, phase) => ({
          duration: phase === 'enter' ? 200 : 100,
        }),
      }) satisfies UseTransitionProps,
    [onDestroyedRef]
  );

  const sharedOverlayProps = {
    ref,
    isTransparent: useTransparentOverlay,
    isCentered,
    onMouseDown,
    'data-testid': 'modal-overlay',
  };

  const transition = useTransition(isOpen, overlayTransition);

  // If we're in popout mode, we need to call onCloseComplete when the component is unmounted since there's no transition
  useEffect(() => {
    return () => {
      if (isPopout) {
        onCloseComplete?.();
      }
    };
  }, [onCloseComplete, isPopout, isOpen]);

  return isPopout
    ? // No transitions for popout mode
      isOpen && <OverlayWrapper {...sharedOverlayProps}>{children}</OverlayWrapper>
    : // Use transitions for normal mode
      transition(
        (style, item) =>
          item && (
            <OverlayWrapper {...sharedOverlayProps} style={style}>
              {children}
            </OverlayWrapper>
          )
      );
});

const modalContextDefaultValue: ModalContextProps = { portalize: true };
const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export interface ModalContextProps {
  portalize: boolean;
}

const InnerModal = forwardRef<HTMLDivElement | null, Omit<InnerModalProps, 'ref'>>(
  (
    {
      children,
      style,
      mt,
      width,
      height,
      useTransparentOverlay,
      autoFocusFirstElement = true,
      disableFocusLock = false,
      close,
      closeOnClickOutside = true,
      preventClosing = false,
      closeOnEscape = true,
      isOpen,
      ...props
    },
    ref
  ) => {
    const innerRef = useForwardedRef<HTMLDivElement>(ref);
    const handleEscape = closeOnEscape && isOpen && !preventClosing;
    const escapeHandler = useCallback(
      (e: KeyboardEvent) => {
        close();
        e.stopPropagation();
      },
      [close]
    );
    useOnKeyDown({ key: 'Escape' }, handleEscape ? escapeHandler : undefined, innerRef);

    useOnClickOutside(innerRef, isOpen && closeOnClickOutside && !preventClosing ? close : undefined);

    const sharedWrapperProps = {
      mt,
      ...props,
      w: width,
      h: height,
      ref: innerRef,
      'aria-modal': !useTransparentOverlay,
      role: 'dialog',
      position: 'relative',
    };

    const sharedFocusLockProps = {
      autoFocus: autoFocusFirstElement,
      disabled: disableFocusLock,
      className: 'focus-lock',
    };
    return (
      <FocusLock {...sharedFocusLockProps}>
        <Wrapper {...sharedWrapperProps} style={style}>
          {children}
        </Wrapper>
      </FocusLock>
    );
  }
);

export function useModalContext() {
  return useContext(ModalContext);
}
